<template>
  <Card
    class="h-full"
    full-height
    no-margin
  >
    <div class="full-calendar-wrapper">
      <!-- Menu -->
      <div
        class="calendar-left h-full overflow-y-auto"
        :class="{ open: menuOpen }"
      >
        <h3 class="p-5">{{ $t('ui.common.filter', 2) }}</h3>
        <div class="p-5 pt-0">
          <Label for="status">{{ $t('ui.common.status', 2) }}</Label>
          <div
            v-for="status in filterArray.statuses"
            :key="status"
            class="mb-1"
          >
            <Checkbox
              v-model="status.isChecked"
              :label="status.label"
              :checkbox-color="status.color"
            />
          </div>
        </div>
        <div class="p-5 pt-0 pb-0">
          <FormGroup
            :label="$t('ui.entities.dealership', 2)"
            label-for="dealerships"
          >
            <Select
              v-model="filterArray.dealershipsSelected"
              mode="multiple"
              searchable
              :options="filterArray.dealershipItems"
              label-key="displayName"
              value-key="id"
              option-group-label="groupName"
              option-group-children="children"
              :chips="false"
              :placeholder="$t('ui.common.all', 2)"
            />
          </FormGroup>
        </div>
        <div class="p-5 pt-0">
          <FormGroup
            :label="$t('ui.entities.appointment.bookedBy')"
            label-for="created-by"
          >
            <Select
              v-model="filterArray.createdBySelected"
              mode="multiple"
              :options="filterArray.createdByItems"
              label-key="fullName"
              value-key="id"
              searchable
              :chips="true"
              :auto-expand="true"
              :placeholder="$t('ui.common.all', 2)"
            />
          </FormGroup>
        </div>
        <div class="p-5 pt-0">
          <Button
            :label="$t('ui.actions.clear', 2)"
            severity="secondary"
            icon="filter-slash"
            outlined
            @click="clearFilters()"
          />
        </div>
      </div>
      <!-- Main Calendar -->
      <div class="calendar-right">
        <FullCalendar
          ref="fullCalendarRef"
          :options="filteredCalendar"
        >
          <template #eventContent="arg">
            <div class="w-full overflow-hidden">
              <fullCalendarContent :appointment-data="arg.event.extendedProps" />
            </div>
          </template>
        </FullCalendar>
      </div>
      <div
        class="calendar-menu-overlay"
        :class="{ open: menuOpen }"
        @click="toggleMenu"
      ></div>
    </div>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useAccessStore } from '@/stores/AccessStore.ts'

import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'

import Button from '@/components/button/Button.vue'
import Card from '@/components/card/Card.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Label from '@/components/forms/Label.vue'
import Select from '@/components/forms/Select.vue'
import fullCalendarContent from '@/components/unique/full-calendar/fullCalendarContent.vue'

import { getDictionaryItem } from '@/dictionary'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const accessStore = useAccessStore()

const dealershipsApiRequest = useApiRequest()
const users = useApiRequest()

const fullCalendarRef = ref(null)

const menuOpen = ref(false)

// Toggle menu logic
function toggleMenu() {
  menuOpen.value = !menuOpen.value
  // console.log('menu open: ' + menuOpen.value);
}

// Set Filter array
const filterArray = ref([])
filterArray.value.statuses = [
  {
    name: 'unassigned',
    label: t('ui.entities.appointment.status.unassigned'),
    isChecked: true,
    color: 'utility-gray-600'
  },
  {
    name: 'assigned',
    label: t('ui.entities.appointment.status.assigned'),
    isChecked: true,
    color: 'utility-blue-900'
  },
  {
    name: 'cancelled',
    label: t('ui.entities.appointment.status.cancelled'),
    isChecked: true,
    color: 'utility-error-600'
  },
  {
    name: 'no-show',
    label: t('ui.entities.appointment.status.noShow'),
    isChecked: true,
    color: 'utility-error-600'
  },
  {
    name: 'completed',
    label: t('ui.entities.appointment.status.completed'),
    isChecked: true,
    color: 'utility-success-600'
  },
  {
    name: 'ongoing',
    label: t('ui.entities.appointment.status.ongoing'),
    isChecked: true,
    color: 'utility-purple-600'
  }
]

// let filterDealershipItems = ref([])

filterArray.value.dealershipItems = ref([])
filterArray.value.dealershipsSelected = ref([])
filterArray.value.createdByItems = ref([])
filterArray.value.createdBySelected = ref([])

// Get users from DB to add to 'Made By' dropdown filter
try {
  users
    .send({
      endpoint: '/v1/users',
      params: { userType: 'retainUsers', forList: true }
    })
    .then(response => {
      filterArray.value.createdByItems = response.data
    })
} catch (err) {
  if (err.response) {
    console.error(err.response.status)
    console.error(err.response.data)
  }
}

/**
 * Groups dealerships by their group name
 * @param {Array} dealerships - Array of dealership objects
 * @returns {Array} Array of grouped dealerships
 */
function groupDealershipsByGroup(dealerships) {
  if (!dealerships || !Array.isArray(dealerships)) return []

  const grouped = {}

  dealerships.forEach(dealership => {
    const groupName = dealership.groupName || 'Other'
    if (!grouped[groupName]) {
      grouped[groupName] = []
    }
    grouped[groupName].push(dealership)
  })

  return Object.keys(grouped)
    .sort()
    .map(groupName => ({
      groupName,
      children: grouped[groupName]
    }))
}

// Get dealerships to use in filter
const dealerships = accessStore.selectedDealershipIds

if (dealerships) {
  try {
    dealershipsApiRequest
      .send({
        endpoint: '/v1/dealerships',
        params: { ids: dealerships }
      })
      .then(response => {
        filterArray.value.dealershipItems = groupDealershipsByGroup(response.data)
      })
  } catch (err) {
    if (err.response) {
      console.error('ERROR: ' + err.response.status)
      console.error('ERROR: ' + err.response.data)
    }
  }
}

// Set default view and date
let calendarInitView = 'dayGridMonth'
let calendarInitDate = dayjs().format('YYYY-MM-DD')
// Check if view and dates are in localstorage
if (localStorage.getItem('calendarInitView')) {
  calendarInitView = localStorage.getItem('calendarInitView')
  calendarInitDate = localStorage.getItem('calendarInitDate')
}

// If view and/or date isset in the route query, override previous values
if (route.query.view) {
  calendarInitView = route.query.view
}
if (route.query.date) {
  calendarInitDate = route.query.date
}
if (route.query.statuses) {
  const calendarInitStatuses = route.query.statuses.split(',')

  filterArray.value.statuses.forEach(filter => {
    filter.isChecked = calendarInitStatuses.includes(filter.name)
  })
}

// Set ls calendarStartDate to null to force getEvent on first load
localStorage.setItem('calendarStartDate', '')

const events = ref([])

const calendarOptions = ref({
  plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
  // height: 10,
  contentHeight: 100,
  views: {
    listDay: {
      type: 'list',
      duration: { days: 1 },
      buttonText: 'list day'
    },
    listTwoMonths: {
      type: 'list',
      duration: { months: 2 },
      buttonText: 'list next 2 months'
    }
  },
  initialView: calendarInitView,
  initialDate: calendarInitDate,
  datesSet: function (dateInfo) {
    // console.log('datesSet Ran')

    // If start and end dates do NOT match ones in Local Stroage, fetch events again for new date range
    if (dateInfo.start.toISOString() !== localStorage.getItem('calendarStartDate') && dealerships) {
      // console.log('Must fetch events')
      getEvents(dateInfo.start.toISOString(), dateInfo.end.toISOString())
    }

    // console.log(dateInfo.start.toISOString() + " = " + dateInfo.end.toISOString(), dateInfo.view.type);
    localStorage.setItem('calendarInitView', dateInfo.view.type)
    localStorage.setItem('calendarInitDate', dateInfo.start.toISOString())
    localStorage.setItem('calendarStartDate', dateInfo.start.toISOString())
    localStorage.setItem('calendarEndDate', dateInfo.end.toISOString())

    // getEvents(dateInfo.start.toISOString(), dateInfo.end.toISOString())
  },
  customButtons: {
    menuToggle: {
      text: useI18n().t('ui.common.filter'),
      click: function () {
        // alert('clicked the custom button!');
        toggleMenu()
      }
    }
  },
  headerToolbar: {
    left: 'menuToggle prev,next today',
    center: 'title',
    right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
  },
  showNonCurrentDates: false,
  eventOverlap: false,
  slotEventOverlap: false,
  locales: allLocales,
  locale: localStorage.getItem('locale'),
  firstDay: 1,
  navLinks: true, // can click day/week names to navigate views
  editable: false, // Disallow moving of events
  nowIndicator: true,
  dayMaxEvents: true, // allow "more" link when too many events
  eventTimeFormat: {
    // like '14:30:00'
    hour: '2-digit',
    minute: '2-digit',
    meridiem: false,
    hour12: false
  },
  fixedWeekCount: false,
  events: events.value,
  eventClick: function (info) {
    info.jsEvent.preventDefault()
    // console.log(info.event.path)
    router.push(info.event.url)
  }
})

// Function to fetch events within the current view
function getEvents(start, end) {
  // console.log('Running getEvents()')

  const urlParams = {
    dealerships: dealerships,
    start: dayjs(start).format('YYYY-MM-DD'),
    end: dayjs(end).format('YYYY-MM-DD')
  }

  if (authStore.roles.includes(10) && authStore.roles.length === 1) {
    // If Sales Exec
    urlParams.assignedTo = authStore.details.id
  }

  // Get events within the specified date range
  try {
    useApiRequest()
      .send({
        endpoint: '/v1/appointments',
        params: urlParams
      })
      .then(response => {
        // Format response data into fullcalendar format
        const formattedEvents = response.data.map(app => {
          const mappedStatus = getDictionaryItem('appointmentStatus', app.status)
          return {
            ...app,
            id: app.id,
            title: app.customerName,
            start: app.time,
            url: '/agreement/' + app.agreementId + '/appointments/' + app.id,
            status: app.status,
            dealershipId: app.dealershipId,
            createdById: app.createdBy?.id,
            statusColor: mappedStatus.color,
            classNames: ['style-appointment', app.status]
          }
        })
        events.value = formattedEvents
      })
  } catch (err) {
    if (err.response) {
      console.error(err.response.status)
      console.error(err.response.data)
    }
  }
}

function clearFilters() {
  // Loop through statuses and set isChecked to true
  filterArray.value.statuses.forEach(status => {
    status.isChecked = true
  })

  // Set dealerships selected to []
  filterArray.value.dealershipsSelected = []

  // Set created by selected to []
  filterArray.value.createdBySelected = []

  router.replace({ query: {} })
}

const filteredCalendar = computed(() => {
  // console.log('computed')

  // console.log(calendarOptions.value.events)

  // Get statuses that should be applied
  const filteredStatuses = filterArray.value.statuses.filter(status => status.isChecked)
  // console.log('Active Statuses: ' + JSON.stringify(filteredStatuses))

  const statusesToApply = ref([])
  filteredStatuses.forEach(status => {
    statusesToApply.value.push(status.name.toLowerCase())
  })
  // console.log('Statuses To Apply: ' + JSON.stringify(statusesToApply.value))

  // Filter based on statuses to apply
  let filteredEvents = []
  filteredEvents = events.value.filter(event => statusesToApply.value.includes(event.status))

  // Filter based on dealerships selected
  // console.log('Dealerships Selected: ' + filterArray.value.dealershipsSelected)
  if (filterArray.value.dealershipsSelected.length != 0) {
    // console.log(filterArray.value.dealershipsSelected)
    filteredEvents = filteredEvents.filter(event => filterArray.value.dealershipsSelected.includes(event.dealershipId))
  }

  // Filter based on created by
  // console.log('Dealerships Selected: ' + filterArray.value.dealershipsSelected)
  if (filterArray.value.createdBySelected.length != 0) {
    // console.log('CreatedBySelected: ' + filterArray.value.createdBySelected)
    // console.log(filterArray.value.createdBySelected)
    filteredEvents = filteredEvents.filter(event => filterArray.value.createdBySelected.includes(event.createdById))
  }

  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  calendarOptions.value.events = filteredEvents

  return calendarOptions.value
})
</script>

<style>
.calendar-left {
  width: 250px;
  flex-shrink: 0;
  border-right: 1px solid var(--border-secondary);
  background: var(--bg-primary);
  z-index: 11;
  transition: all 0.5s ease-in-out;
  border-radius: 0.428rem 0 0 0.428rem;
}

.calendar-left {
  position: absolute;
  left: -251px;
}

.calendar-left.open {
  left: 0;
}

.calendar-right {
  width: 100%;
}

.calendar-menu-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  border-radius: 0.428rem;
  background-color: #00000033;
  visibility: hidden;
  opacity: 0;
  z-index: 10;

  transition: all 0.6s ease-in-out;
}

.calendar-menu-overlay.open {
  visibility: visible;
  opacity: 1;
}

/* DayGrid Custom Styling */
.style-appointment {
  padding-left: 5px;
  font-size: 11px;
  font-weight: 500;
}

/* Appointment Styles */
.style-appointment.unassigned > .fc-daygrid-event-dot,
.style-appointment.unassigned.fc-list-event .fc-list-event-dot {
  border-color: var(--bg-tertiary) !important;
}

.style-appointment.unassigned.fc-timegrid-event {
  background-color: var(--bg-tertiary) !important;
}

.style-appointment.assigned > .fc-daygrid-event-dot,
.style-appointment.assigned.fc-list-event .fc-list-event-dot {
  border-color: var(--utility-blue-800) !important;
}

.style-appointment.assigned.fc-timegrid-event {
  background-color: var(--utility-blue-800) !important;
}

.style-appointment.cancelled > .fc-daygrid-event-dot,
.style-appointment.cancelled.fc-list-event .fc-list-event-dot {
  border-color: var(--utility-error-600) !important;
}

.style-appointment,
.style-appointment.fc-list-event {
  color: var(--text-primary) !important;
}

.style-appointment.cancelled,
.style-appointment.cancelled.fc-list-event {
  color: var(--text-quaternary) !important;
  text-decoration: line-through;
}

.style-appointment.cancelled.fc-timegrid-event {
  background-color: var(--utility-error-300) !important;
  color: var(--text-quaternary) !important;
  text-decoration: line-through;
}

.style-appointment.cancelled.fc-timegrid-event .fc-event-main {
  color: var(--text-quaternary) !important;
}

.style-appointment.completed > .fc-daygrid-event-dot,
.style-appointment.completed.fc-list-event .fc-list-event-dot {
  border-color: var(--utility-success-600) !important;
}

.style-appointment.completed.fc-timegrid-event {
  background-color: var(--utility-success-300) !important;
}

.style-appointment.no-show > .fc-daygrid-event-dot,
.style-appointment.no-show.fc-list-event .fc-list-event-dot {
  border-color: var(--utility-error-600) !important;
}

.style-appointment.no-show.fc-timegrid-event {
  background-color: var(--utility-error-300) !important;
}

.style-appointment.not-dealt > .fc-daygrid-event-dot,
.style-appointment.not-dealt.fc-list-event .fc-list-event-dot {
  border-color: var(--utility-warning-600) !important;
}

.style-appointment.not-dealt.fc-timegrid-event {
  background-color: var(--utility-warning-300) !important;
}

.style-appointment.ongoing > .fc-daygrid-event-dot,
.style-appointment.ongoing.fc-list-event .fc-list-event-dot {
  border-color: var(--utility-purple-600) !important;
}

.style-appointment.ongoing.fc-timegrid-event {
  background-color: var(--utility-purple-300) !important;
}

.style-appointment .fc-list-event-title .month-view-dot {
  display: none;
}

.style-appointment .fc-event-main .month-view-dot {
  display: none;
}

.fc-v-event {
  border: none;
}

.fc .fc-timegrid-col.fc-day-today {
  background: none;
}

.fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
  background: var(--blue);
  color: white;
  border-radius: 20px;
  width: 100px;
}

.fc-popover {
  border-radius: 5px;
  box-shadow: 0 2px 17px rgb(0 0 0 / 35%) !important;
  background: var(--bg-primary) !important;
}

.dark .fc-popover-header {
  padding: 10px 13px !important;
  font-weight: 500;
  background: var(--darkmode-2) !important;
  border: var(--darkmode-2) !important;
  color: var(--secondary) !important;
}

.dark .fc-popover-body {
  padding: 10px 13px !important;
  font-weight: 500;
  background: var(--darkmode-1) !important;
  border: var(--darkmode-1) !important;
}

.fc-theme-standard .fc-list-day-cushion {
  background-color: transparent;
  margin-top: 12px;
}

.appointment-popout-status .popout-unassigned {
  color: var(--utility-gray-600);
}

.appointment-popout-status .popout-assigned {
  color: var(--utility-blue-900);
}

.appointment-popout-status .popout-cancelled {
  color: var(--utility-error-600);
}

.appointment-popout-status .popout-no-show {
  color: var(--utility-error-600);
}

.appointment-popout-status .popout-not-dealt {
  color: var(--utility-warning-600);
}

.appointment-popout-status .popout-completed {
  color: var(--utility-success-600);
}

.appointment-popout-status .popout-ongoing {
  color: var(--utility-purple-600);
}
</style>
